import React, { useCallback, useEffect, useMemo, useState } from "react";

import { addBreadcrumb, captureException } from "@sentry/nextjs";
import type { GetStaticPaths, GetStaticPropsContext } from "next";
import { useRouter } from "next/router";
import toast from "react-hot-toast";

import { ApplicationTimeline } from "components/application-timeline";
import { ApplyPage } from "components/apply-page/apply-page";
import { ApplyPageHeader } from "components/apply-page-header/apply-page-header";
import { ContinueApplicationCard } from "components/continue-application-card";
import { ContinueApplicationModal } from "components/continue-application-modal";
import { Loader } from "components/loader";
import { NavButton } from "components/nav-button";
import { StartApplicationCard } from "components/start-application-card";
import {
  getRequiredSlugsOrThrow,
  legacyGetApplyConfig,
  legacyGetApplyConfigs,
} from "features/program-applications/utils";
import { useUser } from "hooks/use-user";
import { INACTIVE_PROGRAMS } from "lib/constants";
import { usePartnerTheme } from "lib/partner-theme-context";
import { isApplicationComplete, isApplicationSubmitted } from "utils/application-state";
import {
  CONFLICTING_APPLICATION_ALREADY_EXISTS_ERROR,
  EXPIRED_USER_SESSION_ERROR,
  useApplicationStore,
} from "utils/application-store-context";
import { getActiveCohorts } from "utils/cohorts";
import { inferSSRProps } from "utils/infer-ssr-props";
import { unblockExpiredSession } from "utils/session";
import {
  CONFLICTING_SESSION_USER_ALREADY_EXISTS_ERROR,
  INVALID_EMAIL_ERROR,
  useSessionUser,
} from "utils/session-user-context";
import { storage } from "utils/storage";
import { getStepUrl } from "utils/url";

export type PageServerParams = Parameters<typeof CohortsIndexPage>["0"];
const CohortsIndexPage: React.FC<React.PropsWithChildren<Readonly<inferSSRProps<typeof getStaticProps>>>> = ({
  program,
  cohort,
  applySteps,
}) => {
  const { user, loading: userLoading } = useUser();
  const { createApplication, findApplication, loading: applicationsLoading } = useApplicationStore();
  const { createSessionUser } = useSessionUser();
  const router = useRouter();
  const { backgroundImage: PartnerBackgroundImage, name: partnerName } = usePartnerTheme();
  const [isContinueOpen, setIsContinueOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const firstApplyStep = applySteps[0];

  const existingApplication = findApplication(program.slug, cohort.slug);

  const isActiveCohort = getActiveCohorts([cohort]).length > 0;

  const openContinueModal = () => setIsContinueOpen(true);

  const { isComplete, firstUncompletedStep } = useMemo(
    () => isApplicationComplete(applySteps, existingApplication?.data),
    [applySteps, existingApplication?.data],
  );

  const startApplication = useCallback(
    async (email: string): Promise<void> => {
      setIsRedirecting(true);
      try {
        // create a new user session if needed
        if (!user) await createSessionUser(email);
        addBreadcrumb({
          category: "application",
          message: "Creating application",
          data: { email },
        });
        // create application
        await createApplication(program.slug, cohort.slug, {
          email,
          // NOTE(steven): this tracks the utm_program upon application creation for ODF
          // I have this for ODF only because we are only tracking UTM program there,
          // but we can remove this check safely for other programs when they need it as well.
          ...(program.slug === "founders" && {
            utm_program: router?.query?.utm_program ?? storage.getItem("utm_program") ?? undefined,
          }),
        });
        router.push(getStepUrl(program.slug, cohort.slug, firstApplyStep.slug));
      } catch (error) {
        if (
          [CONFLICTING_APPLICATION_ALREADY_EXISTS_ERROR, CONFLICTING_SESSION_USER_ALREADY_EXISTS_ERROR].indexOf(
            error?.message,
          ) >= 0
        ) {
          // An application or a session user already exists for the same email
          // let's redirect to the email verification step, so the user can continue from where they left
          const redirectQuery = new URLSearchParams({
            email: email.toLowerCase(),
            returnTo: `${router.asPath}/continue`,
          });
          await router.push(`/api/auth/login?${redirectQuery.toString()}`);
        } else if (error?.message === EXPIRED_USER_SESSION_ERROR) {
          alert("It has been a while since you last logged in. Please, verify your email address to continue.");
          await unblockExpiredSession(email.toLowerCase());
          const redirectQuery = new URLSearchParams({
            email: email.toLowerCase(),
            returnTo: `${router.asPath}/continue`,
          });
          await router.push(`/api/auth/login?${redirectQuery.toString()}`);
        } else {
          captureException(error);
          switch (error?.message) {
            case INVALID_EMAIL_ERROR:
              toast.error("Email address is invalid.");
              break;
            default:
              toast.error("There was an error starting your application, please try again.");
              break;
          }
          setIsRedirecting(false);
        }
      }
    },
    [program, cohort, createApplication, createSessionUser, router, user, firstApplyStep.slug],
  );

  useEffect(() => {
    // NOTE(valter): These are used by Tripetto
    storage.setItem("program", program.slug);

    router.query.utm_program && storage.setItem("utm_program", router.query.utm_program as string);
    router.query.utm_source && storage.setItem("utm_source", router.query.utm_source as string);
    router.query.utm_medium && storage.setItem("utm_medium", router.query.utm_medium as string);
    router.query.utm_campaign && storage.setItem("utm_campaign", router.query.utm_campaign as string);
  }, [router, program.slug]);

  useEffect(() => {
    if (INACTIVE_PROGRAMS.includes(program.slug) && router.isReady) {
      setIsRedirecting(true);
      router.push(`/programs/${program.slug}/info`);
    }
    if (!existingApplication && !isActiveCohort && router.isReady && !userLoading && !applicationsLoading) {
      setIsRedirecting(true);
      router.push(`/programs/${program.slug}`);
    }
  }, [router, program.slug, existingApplication, isActiveCohort, userLoading, applicationsLoading]);

  // TODO (richard): Remove partner themes logic in apply entirely: https://linear.app/on-deck/issue/A2O-430/deprecate-partner-themes-in-apply
  // NOTE(steven): we must check router.isReady since the partner theme gets set based on URL params;
  // avoids flash of wrong styles.
  if ([userLoading, applicationsLoading, isRedirecting, !router.isReady].some((x) => x)) {
    return (
      <ApplyPage program={program} cohort={cohort} slug="cohort">
        <Loader size="md" />
      </ApplyPage>
    );
  }

  return (
    <ApplyPage
      program={program}
      cohort={cohort}
      slug="cohort"
      header={
        <ApplyPageHeader
          variant={partnerName ? "bare" : "blue"}
          title={`${program.name}`}
          description={
            existingApplication && isApplicationSubmitted(existingApplication)
              ? ""
              : "This application should take you a couple of minutes to complete. The process is as follows:"
          }
          button={
            user ? (
              <NavButton link="/" label="Start a new application" inverse></NavButton>
            ) : (
              <NavButton
                link="#"
                onClick={() => {
                  openContinueModal();
                }}
                label="Continue application"
                inverse
              ></NavButton>
            )
          }
        >
          <div className="mt-10 overflow-x-hidden">
            <ApplicationTimeline
              applySteps={applySteps}
              applyStep={firstUncompletedStep ?? firstApplyStep}
              program={program}
              cohort={cohort}
              inverse
            />
          </div>
        </ApplyPageHeader>
      }
    >
      {PartnerBackgroundImage && <PartnerBackgroundImage />}
      <div className="max-w-xl mx-auto">
        {/* NOTE(Hichem/Richard): applications can be complete (i.e. filled), but not yet submitted (so there is indeed a difference between isComplete and isSubmitted) */}
        {/* A submitted application can also become incomplete (e.g. when we do form changes after a user submitted their application) */}
        {existingApplication && (firstUncompletedStep || isComplete || isApplicationSubmitted(existingApplication)) ? (
          <ContinueApplicationCard
            application={existingApplication}
            program={program}
            cohort={cohort}
            isComplete={isComplete}
            firstUncompletedStep={firstUncompletedStep}
          />
        ) : (
          <StartApplicationCard openContinueModal={openContinueModal} startApplication={startApplication} />
        )}
      </div>
      <ContinueApplicationModal isOpen={isContinueOpen} onClose={() => setIsContinueOpen(false)} />
    </ApplyPage>
  );
};

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
  try {
    // validate slugs (now strongly typed)
    const { programSlug, cohortSlug } = getRequiredSlugsOrThrow(["cohortSlug", "programSlug"], params);
    // get apply config via hard-coded legacy files
    const applyConfig = await legacyGetApplyConfig({ byProgramSlug: programSlug, byCohortSlug: cohortSlug });
    if (!applyConfig) return { notFound: true };

    // ✅ returning apply-config
    return {
      props: applyConfig,
    };
  } catch (e) {
    if (e instanceof TypeError) {
      captureException(
        new Error(`[getStaticProps] Required slugs not provided on this route`, { cause: e }),
        // note (richard): This shouldn't happen at all
        { level: "error" },
      );
    } else {
      // note (richard): this could happen any time
      captureException(new Error("Unable to provide apply-config at build time", { cause: e }), { level: "info" });
    }
    return { notFound: true };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = [];
  const { applyConfigs } = await legacyGetApplyConfigs();

  for (const applyConfigProgram of applyConfigs) {
    for (const cohort of applyConfigProgram.cohorts) {
      paths.push(`/programs/${applyConfigProgram.slug}/cohorts/${cohort.slug}`);
    }
  }
  return { paths, fallback: false };
};

export default CohortsIndexPage;
